import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ParentChildContext = createContext(null);

/**
 * Provider for the currently active child a parent is focused on.
 *
 * - Only fetches children if the logged-in user is a parent.
 * - Persists the selection in localStorage under `leranio.parent.activeChild`.
 * - Exposes: children (list), activeChildId ("all" or child id),
 *   activeChild (single object or null when "all"), setActiveChildId(id), refresh().
 *
 * Non-parent users get: children=[] and activeChildId="all" (harmless no-op).
 */
export function ParentChildProvider({ children }) {
  const { user } = useAuth();
  const [kids, setKids] = useState([]);
  const [activeChildId, setActiveChildId] = useState(() => localStorage.getItem("leranio.parent.activeChild") || "all");
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (user?.role !== "parent") { setKids([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/users/my-children");
      setKids(data.items || []);
    } catch {
      setKids([]);
    } finally { setLoading(false); }
  }, [user?.role]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    localStorage.setItem("leranio.parent.activeChild", activeChildId);
  }, [activeChildId]);

  useEffect(() => {
    // Aktif çocuk artık listede yoksa "all" fallback
    if (activeChildId !== "all" && kids.length > 0 && !kids.find((c) => c.id === activeChildId)) {
      setActiveChildId("all");
    }
  }, [kids, activeChildId]);

  const value = useMemo(() => ({
    isParent: user?.role === "parent",
    children: kids,
    hasMultiple: kids.length > 1,
    activeChildId,
    activeChild: activeChildId === "all" ? null : kids.find((c) => c.id === activeChildId) || null,
    setActiveChildId,
    refresh,
    loading,
  }), [user?.role, kids, activeChildId, refresh, loading]);

  return <ParentChildContext.Provider value={value}>{children}</ParentChildContext.Provider>;
}

export function useParentChild() {
  const ctx = useContext(ParentChildContext);
  if (!ctx) return { isParent: false, children: [], hasMultiple: false, activeChildId: "all", activeChild: null, setActiveChildId: () => {}, refresh: () => {}, loading: false };
  return ctx;
}
